// Computer Networks interview questions data
export const cnQuestions = {
  'basics': [
    {
      id: 1,
      question: "What is a computer network?",
      answer: "A computer network is devices (PCs, phones, printers, routers) linked so they can talk and share things, most often internet access or files and printers on a LAN."
    },
    {
      id: 2,
      question: "What is a node and a link?",
      answer: "A node is anything on the network that can send or receive traffic (laptop, printer, router). A link is what connects two nodes: Ethernet cable, fiber, Wi-Fi radio path, etc."
    },
    {
      id: 3,
      question: "What are the types of computer networks?",
      answer: "LAN (Local Area Network): one building or campus, usually fast and private. MAN (Metropolitan Area Network): city-scale links. WAN (Wide Area Network): long distance, often through carriers; the global Internet is the biggest WAN."
    },
    {
      id: 4,
      question: "What is network topology?",
      answer: "Topology is how devices are laid out logically or physically: bus, star, ring, mesh, hybrid. Each layout trades cost, redundancy, and how a single failure hurts the whole net."
    },
    {
      id: 5,
      question: "Difference between LAN, MAN, and WAN.",
      answer: "LAN is small, private, high speed. MAN spans a city or large campus. WAN crosses regions or countries, rides ISP links, and usually adds more delay and cost than a LAN."
    },
    {
      id: 6,
      question: "What is bandwidth?",
      answer: "Bandwidth is how much data the link can carry per second, measured in bits per second (bps). More bandwidth means more room for video, files, and many users at once."
    },
    {
      id: 7,
      question: "What is latency?",
      answer: "Latency is the delay from sending a packet until it reaches the other side, often in milliseconds. High latency feels like lag in calls or games; low latency feels instant."
    },
    {
      id: 8,
      question: "What is a protocol in networking?",
      answer: "A protocol is an agreed rule set so two systems can exchange data reliably. Examples: TCP and UDP for transport, HTTP/HTTPS for web, FTP for files, DNS for names to IP addresses."
    }
  ],

  'osi-tcpip': [
    {
      id: 1,
      question: "What is the OSI model?",
      answer: "The OSI model is a seven-layer picture of networking: Physical, Data Link, Network, Transport, Session, Presentation, Application. It helps vendors and engineers talk about where a feature lives, even if real stacks merge some layers."
    },
    {
      id: 2,
      question: "Explain each layer of the OSI model.",
      answer: "Physical: raw bits on the wire or air. Data Link: hop-to-hop delivery with MAC addresses. Network: routing between subnets with IP. Transport: end-to-end sessions with TCP or UDP. Session: long-lived dialog control. Presentation: encoding and encryption shape. Application: what users see (HTTP, SMTP, etc.)."
    },
    {
      id: 3,
      question: "What is the TCP/IP model?",
      answer: "TCP/IP is the four-layer model the internet actually uses: Network Access (physical plus link), Internet (IP routing), Transport (TCP/UDP), Application (HTTP, DNS, and everything user-facing rolled together)."
    },
    {
      id: 4,
      question: "Difference between OSI and TCP/IP model.",
      answer: "OSI splits ideas into seven layers for teaching. TCP/IP merges layers into four for implementation. Session and Presentation from OSI mostly live inside the Application layer in TCP/IP."
    },
    {
      id: 5,
      question: "At which OSI layer does a router operate?",
      answer: "A router works at Layer 3 (Network) using IP addresses to move packets between networks. A typical switch is Layer 2 (Data Link) with MAC addresses. A hub is Layer 1 (Physical) and repeats bits blindly."
    },
    {
      id: 6,
      question: "What is the role of the Transport Layer?",
      answer: "Transport delivers data end-to-end between programs on two hosts. TCP adds reliability, ordering, and flow control. UDP is lighter: no built-in reliability, good when speed beats perfect delivery (streaming, DNS queries)."
    },
    {
      id: 7,
      question: "What happens when you type a URL in a browser?",
      answer: "Rough flow: DNS turns the hostname into an IP, TCP does a three-way handshake to that IP, the browser sends an HTTP or HTTPS request, the server sends the page or API data, then the browser renders it. Each step maps to different layers (DNS at app, TCP at transport, IP at network, etc.)."
    }
  ],

  'ip-addressing': [
    {
      id: 1,
      question: "What is an IP address?",
      answer: "An IP address is the numeric ID of a host on an IP network so routers know where to deliver packets. IPv4 is 32 bits (dotted decimal like 192.168.1.1). IPv6 is 128 bits in hexadecimal with colons."
    },
    {
      id: 2,
      question: "Difference between IPv4 and IPv6.",
      answer: "IPv4 is 32-bit with about 4.3 billion addresses, shown in dotted decimal. IPv6 is 128-bit with a vastly larger space and colon hex notation. IPv6 was introduced because the world ran short of IPv4 addresses."
    },
    {
      id: 3,
      question: "What is subnetting?",
      answer: "Subnetting splits one IP block into smaller networks for routing, security, and broadcast size control. A subnet mask (or prefix length) tells you which bits are the network part and which identify the host."
    },
    {
      id: 4,
      question: "What is a subnet mask?",
      answer: "A subnet mask marks the split between network bits and host bits in IPv4. Example: 255.255.255.0 is /24, meaning the first 24 bits name the subnet and the last 8 bits name hosts inside it."
    },
    {
      id: 5,
      question: "Difference between public and private IP address.",
      answer: "Public IPs are unique on the internet and route globally (for example a server’s address). Private IPs are reused inside homes and offices (10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16) and are not routed on the public internet as source addresses."
    },
    {
      id: 6,
      question: "What is NAT (Network Address Translation)?",
      answer: "NAT lets many private devices share one public IP. The home or office router rewrites source addresses to its public IP on the way out, and maps return traffic back to the correct private host using connection tables and ports."
    },
    {
      id: 7,
      question: "What is CIDR (Classless Inter-Domain Routing)?",
      answer: "CIDR writes networks as address plus prefix length, like 192.168.1.0/24. The /24 says how many leading bits are the network. It replaced old fixed \"class A/B/C\" sizes so address space is not wasted."
    },
    {
      id: 8,
      question: "What is a loopback address?",
      answer: "127.0.0.1 (IPv4 loopback) always points to this same machine. Pinging it checks that your own TCP/IP stack works without needing another host on the LAN."
    }
  ],

  'protocols': [
    {
      id: 1,
      question: "What is DNS?",
      answer: "DNS (Domain Name System) maps friendly names like example.com to IP addresses so programs can open connections without making humans memorize numbers. Think phonebook for the internet."
    },
    {
      id: 2,
      question: "What is DHCP?",
      answer: "DHCP (Dynamic Host Configuration Protocol) hands out IP address, mask, default gateway, and DNS settings automatically when a device joins the network, so users rarely type network details by hand."
    },
    {
      id: 3,
      question: "What is ARP?",
      answer: "ARP (Address Resolution Protocol) answers \"which MAC owns this IP on this LAN?\" A host broadcasts a request; the owner replies with its MAC so Ethernet frames can be addressed correctly at Layer 2."
    },
    {
      id: 4,
      question: "What is HTTP vs HTTPS?",
      answer: "HTTP moves web data in clear text on the wire. HTTPS is HTTP wrapped in TLS encryption so eavesdroppers see ciphertext, not passwords or pages. Browsers show the padlock for HTTPS."
    },
    {
      id: 5,
      question: "What is FTP?",
      answer: "FTP (File Transfer Protocol) moves files between client and server, classically with a control channel on port 21 and a data channel on port 20. SFTP/FTPS add encryption for safer file transfer."
    },
    {
      id: 6,
      question: "What is SMTP?",
      answer: "SMTP (Simple Mail Transfer Protocol) is how mail clients and servers hand off outgoing email, commonly ports 25 (relay) or 587 (submission with STARTTLS)."
    },
    {
      id: 7,
      question: "What is ICMP and what is it used for?",
      answer: "ICMP (Internet Control Message Protocol) carries control and error messages for IP, such as \"host unreachable.\" ping uses ICMP echo requests; traceroute uses ICMP time exceeded or TTL tricks to map hops."
    },
    {
      id: 8,
      question: "What is a MAC address?",
      answer: "A MAC (Media Access Control) address is a 48-bit hardware ID burned into (or assigned to) a NIC. Layer 2 switches use MAC addresses to deliver frames on the same LAN segment."
    }
  ],

  'tcp-udp': [
    {
      id: 1,
      question: "Difference between TCP and UDP.",
      answer: "TCP is connection-oriented, reliable, and ordered: good for web, email, file copy. UDP is connectionless and best-effort: smaller overhead, good for live video, VoIP, DNS, and games where dropping a late packet beats waiting."
    },
    {
      id: 2,
      question: "What is the TCP 3-way handshake?",
      answer: "Client sends SYN, server answers SYN-ACK, client sends ACK. That agrees on starting sequence numbers and opens a TCP connection before application data flows."
    },
    {
      id: 3,
      question: "What is TCP flow control?",
      answer: "Flow control stops the sender from overrunning the receiver’s buffer. The receiver advertises a window size; the sender may only have that many unacknowledged bytes in flight."
    },
    {
      id: 4,
      question: "What is TCP congestion control?",
      answer: "Congestion control slows or speeds the sender based on network signals like packet loss or delay. Classic pieces include slow start, congestion avoidance, fast retransmit, and fast recovery so many flows share links fairly."
    },
    {
      id: 5,
      question: "What is a port number?",
      answer: "A port is a 16-bit number on a host that tells the OS which application should get the packet. Examples: HTTP 80, HTTPS 443, SSH 22, DNS 53, SMTP 25."
    },
    {
      id: 6,
      question: "What is a socket?",
      answer: "A socket is usually the pair (IP address, port) that identifies one endpoint of a TCP or UDP conversation. Client and server each have a socket; together they define the connection tuple."
    },
    {
      id: 7,
      question: "What happens if a SYN-ACK is lost in the TCP handshake?",
      answer: "The client never sees the SYN-ACK, so after a timeout it resends SYN. This repeats until a limit is hit and the attempt aborts, or the server’s SYN-ACK finally arrives."
    }
  ],

  'network-devices': [
    {
      id: 1,
      question: "Difference between hub, switch, and router.",
      answer: "Hub repeats every frame to all ports (Layer 1, dumb and collision-prone). Switch learns MACs and forwards only to the right port (Layer 2). Router connects IP subnets and chooses next hop by IP (Layer 3)."
    },
    {
      id: 2,
      question: "What is a gateway?",
      answer: "A gateway is the router or device your host uses to reach other networks, often the \"default gateway\" on a home LAN that leads to the ISP and the internet."
    },
    {
      id: 3,
      question: "What is a modem?",
      answer: "A modem converts between digital signals in your home network and the analog or ISP-specific signaling on the phone, cable, or fiber line so your traffic can ride the provider’s physical plant."
    },
    {
      id: 4,
      question: "What is a firewall?",
      answer: "A firewall filters traffic by rules (allow/deny ports, IPs, apps). It blocks unwanted inbound connections and can log or shape outbound traffic for security policy."
    },
    {
      id: 5,
      question: "What is a proxy server?",
      answer: "A proxy sits between clients and the internet, forwarding requests so the server often sees the proxy’s IP instead of each user’s. Proxies can cache content, filter sites, or enforce corporate access rules."
    },
    {
      id: 6,
      question: "What is a repeater?",
      answer: "A repeater boosts or regenerates a signal at Layer 1 to reach farther on the same medium. It does not inspect addresses; it just strengthens bits."
    }
  ],

  'routing-switching': [
    {
      id: 1,
      question: "What is routing?",
      answer: "Routing is how routers pick paths so packets from a source reach the right destination across many networks, using routing tables built by static config or dynamic protocols."
    },
    {
      id: 2,
      question: "Difference between static and dynamic routing.",
      answer: "Static routes are typed by hand and stay until you change them. Dynamic routing (OSPF, BGP, RIP, etc.) lets routers advertise topology so paths update when links fail or new subnets appear."
    },
    {
      id: 3,
      question: "What is a routing table?",
      answer: "Each router keeps a routing table: destination prefixes, next-hop router or interface, and metrics. For each packet the router longest-matches the destination IP to pick where to forward."
    },
    {
      id: 4,
      question: "What is OSPF?",
      answer: "OSPF (Open Shortest Path First) is a link-state interior gateway protocol. Routers share topology maps and run Dijkstra’s algorithm to pick shortest paths inside one organization’s network."
    },
    {
      id: 5,
      question: "What is BGP?",
      answer: "BGP (Border Gateway Protocol) exchanges reachability between autonomous systems (ISPs and big networks). Policy-rich path choice between organizations is why people say BGP \"runs the internet\" between providers."
    },
    {
      id: 6,
      question: "What is a VLAN?",
      answer: "A VLAN (Virtual LAN) splits one physical switch fabric into separate broadcast domains by tag, so finance and guest Wi-Fi can be isolated without buying separate hardware per group."
    },
    {
      id: 7,
      question: "What is the difference between routing and switching?",
      answer: "Switching forwards frames inside one LAN using MAC addresses (Layer 2). Routing moves packets between IP subnets using Layer 3 headers. You need routing to leave your local subnet."
    }
  ],

  'security': [
    {
      id: 1,
      question: "What is a DoS attack?",
      answer: "Denial of Service means flooding a target with traffic or expensive work so real users cannot get service. DDoS (Distributed) uses many compromised machines to multiply volume."
    },
    {
      id: 2,
      question: "What is a man-in-the-middle attack?",
      answer: "MITM is when an attacker sits between two parties, reading or changing messages while both think they talk directly. TLS on HTTPS is the main fix on the web because it encrypts and authenticates the server."
    },
    {
      id: 3,
      question: "What is SSL/TLS?",
      answer: "TLS (Transport Layer Security), older people still say SSL, encrypts and authenticates a channel between client and server. HTTPS is HTTP over TLS."
    },
    {
      id: 4,
      question: "What is a VPN?",
      answer: "A VPN (Virtual Private Network) wraps your traffic in encryption to a VPN server so observers on the café Wi-Fi see gibberish and your public IP may appear as the server’s. Also used to reach a company intranet remotely."
    },
    {
      id: 5,
      question: "What is a SYN flood attack?",
      answer: "The attacker sends many SYNs without finishing handshakes, filling the server’s half-open connection table or state so real users cannot open new TCP connections."
    }
  ]
};

// Helper function to get all questions from all CN topics
export const getAllCnQuestions = () => {
  const allQuestions = [];
  Object.keys(cnQuestions).forEach(topic => {
    cnQuestions[topic].forEach(q => {
      allQuestions.push({ ...q, topic });
    });
  });
  return allQuestions;
};

// Helper function to shuffle array
export const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
